import { omit } from 'lodash';

import { Tooltip } from 'core/elements';
import { IPricingSourceData } from 'quotes/types';
import { isSpaSourceInUse } from 'quotes/utils';
import { Color, TooltipPosition } from 'theme/enums';

import { ExternalPricingSourceContent } from './ExternalPricingSourceContent';

interface IProps {
  // From getEffectiveSpaInfo on the cost side.
  pricingData: IPricingSourceData;
  spaCostCurrency?: string | null;
  isAlternateVersionEnabled?: boolean;
  children: JSX.Element;
  color?: Color;
}

export const ExternalPricingSourceTooltip = ({
  pricingData,
  spaCostCurrency,
  isAlternateVersionEnabled = false,
  children,
  color = Color.WARNING,
}: IProps) => {
  const displayFields = omit(pricingData, 'sourceType');

  // Stale external fields survive a switch to a non-SPA source, so the source type — not just the
  // presence of data — decides whether this tooltip belongs. `pickPricingSourceFields` drops null
  // values, hence sourceType is undefined (not null) when the API reports no source.
  // Covers a selected SPA too; sell-price callers are unaffected, as no price source is a SPA.
  if (
    !isAlternateVersionEnabled ||
    !isSpaSourceInUse(pricingData.sourceType) ||
    Object.values(displayFields).every((value) => value == null || value === '')
  ) {
    return children;
  }

  return (
    <Tooltip
      title={
        <ExternalPricingSourceContent
          pricingData={displayFields}
          spaCostCurrency={spaCostCurrency}
        />
      }
      color={color}
      placement={TooltipPosition.TOP}
      sx={{ width: '100%', maxWidth: '400px' }}
    >
      {children}
    </Tooltip>
  );
};
